import { Badge } from "reactstrap";
import React from "react";

// The "What's new" guided tour. One flat list of steps, one counter, spanning
// two pages: `page` says which one a step belongs to, and the controller pauses
// the tour while the user is anywhere else. It starts on the site the user
// opened on the World Map, then takes them to Analytics on that same site.
//
// Targets are existing class names of the components themselves, so nothing has
// to be tagged in the markup. Renaming one of them breaks its step silently.
const Title = () => (
  <Badge className="hiphen-badge active w-100">What&apos;s new</Badge>
);

const Step = ({ heading, children }) => (
  <div>
    <p className="mb-1 fw-bold">{heading}</p>
    {children}
  </div>
);

const step = (page, target, placement, heading, content) => ({
  page,
  target,
  placement,
  disableBeacon: true,
  title: <Title />,
  content: <Step heading={heading}>{content}</Step>,
});

// The compass and the dial sit at the bottom edge of the screen: "left-end"
// grows their tooltip upwards from there, a centred one ran off the bottom.
//
// Both ways move the tour on: the user's own click on the compass (the
// controller listens for it), or Next, after which the dial step opens the dial
// itself.
const COMPASS_STEP = {
  ...step(
    "map",
    ".north-indicator",
    "left-end",
    "Rotate the map",
    <>
      The map can now turn to match your own bearing on the field.{" "}
      <strong>Click the compass</strong>, or <strong>Next</strong>, to open the
      rotation dial.
    </>
  ),
  advanceOnClick: true,
};

// A step with `open` shows a panel the user has not opened yet: the controller
// runs it before the step, and only shows the step once it resolves. It clicks
// the app's own controls, as the user would, so nothing has to be wired into
// the components. `cancelled` tells it the step is gone (the user left the
// page), so it stops waiting.
//
// What it opens renders a frame or more after the click, hence the polling.
const waitFor = (find, cancelled) =>
  new Promise((resolve) => {
    const poll = () => {
      if (cancelled()) return;
      const element = find();
      if (element) resolve(element);
      else requestAnimationFrame(poll);
    };
    poll();
  });

// Lets a panel finish sliding in (the Field Book drawer takes 0.16 s, the
// dial 0.2 s) before Joyride measures it for the spotlight.
const SETTLE_MS = 250;
const settle = () => new Promise((resolve) => setTimeout(resolve, SETTLE_MS));

// Opens the Trial menu of the layer control on its Plot Map tab, where the
// Field Book colours the plots.
const openPlotMap = async (cancelled) => {
  const trialMenu = await waitFor(
    () => document.querySelector(".map-layer-control-header-item"),
    cancelled
  );
  if (!trialMenu.classList.contains("active")) trialMenu.click();
  const plotMap = await waitFor(
    () =>
      [...document.querySelectorAll(".trial-card")].find(
        (card) => card.textContent === "Plot Map"
      ),
    cancelled
  );
  if (!plotMap.classList.contains("selected")) plotMap.click();
  await settle();
};

const openFieldBookDrawer = async (cancelled) => {
  const card = await waitFor(
    () => document.querySelector(".fieldbook-card"),
    cancelled
  );
  card.click();
  await settle();
};

// Reached by Next, the dial is not open yet: click the compass as the user
// would. Reached by the user's own click, it already is. Either way, wait for
// its opening animation, which starts it at a third of its size: measured
// during it, the tooltip landed on top of the dial.
const DIAL_STEP = {
  ...step(
    "map",
    ".rotation-dial",
    "left-end",
    "The rotation dial",
    <>
      Drag the ring to turn the map. <strong>Shift + mouse wheel</strong> turns
      the map from anywhere, <strong>N</strong> opens or closes the dial, and{" "}
      <strong>Reset</strong> brings north back up.
    </>
  ),
  // Closed during the step, the tour moves on to the next one (controller).
  nextIfClosed: true,
  open: async (cancelled) => {
    if (!document.querySelector(".rotation-dial"))
      document.querySelector(".north-indicator")?.click();
    await waitFor(() => document.querySelector(".rotation-dial"), cancelled);
    await settle();
  },
};

const mapFieldBookStep = (analytics) => ({
  ...step(
    "map",
    ".map-layer-control",
    "right",
    "Attribute colors",
    <>
      Open <strong>Trial</strong>, then the <strong>Plot Map</strong> tab. Click
      an attribute to colour the plots by its values, and{" "}
      <strong>Edit colors</strong> to pick the colour of each value.
      {analytics && (
        <>
          {" "}
          <strong>Next</strong> opens this site on Analytics.
        </>
      )}
    </>
  ),
  open: openPlotMap,
});

const ANALYTICS_FIELD_BOOK_STEP = {
  ...step(
    "analytics",
    ".fieldbook-drawer",
    "right",
    "Attribute colors on Analytics",
    <>
      The same panel opens from the <strong>Attribute colors</strong> card:
      click an attribute to colour the plots of the breeder report map, and{" "}
      <strong>Edit colors</strong> to pick the colour of each value.
    </>
  ),
  open: openFieldBookDrawer,
};

// `analytics` is false for a site the user cannot open on Analytics (no role,
// or no results): the breeder report would redirect them away, so the tour ends
// on the map.
export const buildTourSteps = ({ analytics }) => [
  COMPASS_STEP,
  DIAL_STEP,
  mapFieldBookStep(analytics),
  ...(analytics ? [ANALYTICS_FIELD_BOOK_STEP] : []),
];
